import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  listRegistryDevices,
  listRegistryVehicles,
  parseTokenClaims,
  RegistryDevice,
  RegistryVehicle,
  upsertRegistryDevice,
} from "../api";

export function DevicesPage() {
  const { t } = useTranslation(["dashboard", "common"]);
  const claims = parseTokenClaims();
  const myFirm = claims?.firm_id ?? "";
  const [devices, setDevices] = useState<RegistryDevice[]>([]);
  const [vehicles, setVehicles] = useState<RegistryVehicle[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [id, setId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [hardwareId, setHardwareId] = useState("");

  async function refresh() {
    try {
      const [d, v] = await Promise.all([
        listRegistryDevices({ firm_id: myFirm }),
        listRegistryVehicles(myFirm),
      ]);
      setDevices(d);
      setVehicles(v);
      setError(null);
    } catch (exc) {
      setError(String(exc));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onCreate(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await upsertRegistryDevice({
        id,
        firm_id: myFirm,
        vehicle_id: vehicleId || null,
        hardware_id: hardwareId || null,
        active: true,
      });
      setId("");
      setHardwareId("");
      refresh();
    } catch (exc) {
      setError(String(exc));
    }
  }

  return (
    <div className="page">
      <h2>{t("devices.title")}</h2>
      <p className="muted">{t("common:shell.firm", { id: myFirm })}</p>
      {error && <p className="error">{error}</p>}

      <form onSubmit={onCreate} className="row-form">
        <input placeholder={t("devices.idPlaceholder")} value={id}
               onChange={(e) => setId(e.target.value)} required />
        <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
          <option value="">{t("devices.noVehicle")}</option>
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>{v.plate || v.id}</option>
          ))}
        </select>
        <input placeholder={t("devices.hardwarePlaceholder")} value={hardwareId}
               onChange={(e) => setHardwareId(e.target.value)} />
        <button type="submit">{t("common:actions.createUpdate")}</button>
      </form>

      {loading ? (
        <p>{t("common:actions.loading")}</p>
      ) : devices.length === 0 ? (
        <p>{t("devices.empty")}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t("devices.th.id")}</th>
              <th>{t("devices.th.vehicle")}</th>
              <th>{t("devices.th.hardware")}</th>
              <th>{t("devices.th.active")}</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.id}>
                <td>{d.id}</td>
                <td>{d.vehicle_id ?? "-"}</td>
                <td>{d.hardware_id ?? "-"}</td>
                <td>{d.active ? t("common:bool.yes") : t("common:bool.no")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
